import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Activity,
  Users,
  Search as SearchIcon,
  Eye,
  TrendingUp,
  Clock,
  UserCheck,
  UserX,
  CalendarIcon,
  X,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from 'recharts';
import { format, subDays, startOfDay, differenceInDays, startOfMonth } from 'date-fns';
import { cn } from '@/lib/utils';

interface ActivityRecord {
  id: string;
  user_id: string;
  activity_type: string;
  metadata: Record<string, unknown>;
  page_path: string | null;
  created_at: string;
}

interface UserProfile {
  id: string;
  email: string;
  full_name: string | null;
  created_at: string;
}

const ACTIVITY_COLORS: Record<string, string> = {
  page_view: '#6366f1',
  search: '#f59e0b',
  monitoring_create: '#10b981',
  monitoring_run: '#14b8a6',
  monitoring_toggle: '#8b5cf6',
  monitoring_delete: '#ef4444',
  result_view: '#3b82f6',
  cpv_search: '#ec4899',
  settings_update: '#6b7280',
  support_ticket: '#ef4444',
  alert_create: '#f97316',
  alert_toggle: '#a855f7',
  export_pdf: '#06b6d4',
  login: '#22c55e',
};

const CHART_COLORS = ['#6366f1', '#f59e0b', '#10b981', '#3b82f6', '#ec4899', '#ef4444', '#8b5cf6', '#14b8a6'];

export default function AdminActivityMonitoring() {
  const { t } = useTranslation();
  const { role, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [activities, setActivities] = useState<ActivityRecord[]>([]);
  const [profiles, setProfiles] = useState<Map<string, UserProfile>>(new Map());
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30');
  const [customDateFrom, setCustomDateFrom] = useState<Date | undefined>(undefined);
  const [customDateTo, setCustomDateTo] = useState<Date | undefined>(undefined);
  const [filterType, setFilterType] = useState('all');
  const [filterUser, setFilterUser] = useState('all');
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    if (authLoading) return;
    if (role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [role, authLoading, navigate, dateRange, customDateFrom, customDateTo]);

  const fetchData = async () => {
    setLoading(true);
    try {
      let since: string;
      let until: string | undefined;
      if (dateRange === 'custom' && customDateFrom) {
        since = startOfDay(customDateFrom).toISOString();
        until = customDateTo ? new Date(customDateTo.getTime() + 86400000).toISOString() : undefined;
      } else {
        since = subDays(new Date(), parseInt(dateRange) || 7).toISOString();
      }

      let query = supabase
        .from('user_activities')
        .select('*')
        .gte('created_at', since)
        .order('created_at', { ascending: false })
        .limit(5000);
      if (until) query = query.lte('created_at', until);

      const [activitiesRes, profilesRes] = await Promise.all([
        query as any,
        supabase
          .from('profiles')
          .select('id, email, full_name, created_at'),
      ]);

      if (activitiesRes.data) setActivities(activitiesRes.data);
      if (profilesRes.data) {
        const map = new Map<string, UserProfile>();
        profilesRes.data.forEach((p: UserProfile) => map.set(p.id, p));
        setProfiles(map);
      }
    } catch (err) {
      console.error('Error fetching activity data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Filtered activities
  const filtered = useMemo(() => {
    let result = activities;
    if (filterType !== 'all') result = result.filter(a => a.activity_type === filterType);
    if (filterUser !== 'all') result = result.filter(a => a.user_id === filterUser);
    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      result = result.filter(a => {
        const profile = profiles.get(a.user_id);
        const email = profile?.email?.toLowerCase() || '';
        const name = profile?.full_name?.toLowerCase() || '';
        const path = a.page_path?.toLowerCase() || '';
        const meta = JSON.stringify(a.metadata || {}).toLowerCase();
        const label = getActivityLabel(a.activity_type).toLowerCase();
        return email.includes(q) || name.includes(q) || path.includes(q) || meta.includes(q) || label.includes(q);
      });
    }
    return result;
  }, [activities, filterType, filterUser, searchText, profiles]);

  // Unique users with activities
  const activeUserIds = useMemo(() => new Set(activities.map(a => a.user_id)), [activities]);

  // Activity types breakdown
  const activityBreakdown = useMemo(() => {
    const counts: Record<string, number> = {};
    filtered.forEach(a => {
      counts[a.activity_type] = (counts[a.activity_type] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([name, value]) => ({ name: getActivityLabel(name), value, key: name }))
      .sort((a, b) => b.value - a.value);
  }, [filtered]);

  // Daily activity trend
  const dailyTrend = useMemo(() => {
    let days: number;
    if (dateRange === 'custom' && customDateFrom) {
      const end = customDateTo || new Date();
      days = Math.max(1, differenceInDays(end, customDateFrom) + 1);
    } else {
      days = parseInt(dateRange) || 7;
    }
    const baseDate = dateRange === 'custom' && customDateTo ? customDateTo : new Date();
    const buckets: Record<string, number> = {};
    for (let i = 0; i < days; i++) {
      const day = format(subDays(baseDate, i), 'MM/dd');
      buckets[day] = 0;
    }
    filtered.forEach(a => {
      const day = format(new Date(a.created_at), 'MM/dd');
      if (buckets[day] !== undefined) buckets[day]++;
    });
    return Object.entries(buckets)
      .map(([date, count]) => ({ date, count }))
      .reverse();
  }, [filtered, dateRange, customDateFrom, customDateTo]);

  // Top users by activity
  const topUsers = useMemo(() => {
    const counts: Record<string, number> = {};
    filtered.forEach(a => {
      counts[a.user_id] = (counts[a.user_id] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([userId, count]) => {
        const profile = profiles.get(userId);
        return {
          userId,
          name: profile?.full_name || profile?.email || userId.slice(0, 8),
          email: profile?.email || '',
          count,
        };
      })
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  }, [filtered, profiles]);

  // Users who signed up but never did anything meaningful
  const inactiveUsers = useMemo(() => {
    const meaningfulTypes = new Set(['search', 'monitoring_create', 'monitoring_run', 'cpv_search', 'export_pdf']);
    const usersWithMeaningful = new Set<string>();
    activities.forEach(a => {
      if (meaningfulTypes.has(a.activity_type)) usersWithMeaningful.add(a.user_id);
    });
    return Array.from(profiles.values())
      .filter(p => !usersWithMeaningful.has(p.id))
      .map(p => ({
        ...p,
        hasAnyActivity: activeUserIds.has(p.id),
      }));
  }, [activities, profiles, activeUserIds]);

  // Hourly distribution
  const hourlyDistribution = useMemo(() => {
    const hours = Array.from({ length: 24 }, (_, i) => ({ hour: `${i.toString().padStart(2, '0')}:00`, count: 0 }));
    filtered.forEach(a => {
      const h = new Date(a.created_at).getHours();
      hours[h].count++;
    });
    return hours;
  }, [filtered]);

  // Unique activity types
  const activityTypes = useMemo(() => {
    const types = new Set(activities.map(a => a.activity_type));
    return Array.from(types).sort();
  }, [activities]);

  // Unique users list for filter
  const usersList = useMemo(() => {
    return Array.from(profiles.values())
      .map(p => ({ id: p.id, label: p.full_name || p.email }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [profiles]);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-foreground">
                {t('admin.activityMonitoring', 'Monitoraggio Attività')}
              </h1>
              <p className="text-muted-foreground">
                {t('admin.activityMonitoringDesc', 'Analizza il comportamento degli utenti e l\'adozione del servizio')}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Select value={dateRange} onValueChange={(v) => { setDateRange(v); if (v !== 'custom') { setCustomDateFrom(undefined); setCustomDateTo(undefined); } }}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Oggi</SelectItem>
                  <SelectItem value="7">7 giorni</SelectItem>
                  <SelectItem value="30">30 giorni</SelectItem>
                  <SelectItem value="90">90 giorni</SelectItem>
                  <SelectItem value="custom">Personalizzato</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Tipo attività" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte le attività</SelectItem>
                  {activityTypes.map(type => (
                    <SelectItem key={type} value={type}>{getActivityLabel(type)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterUser} onValueChange={setFilterUser}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Utente" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti gli utenti</SelectItem>
                  {usersList.map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Custom date range + text search row */}
          <div className="flex flex-wrap items-center gap-2">
            {dateRange === 'custom' && (
              <>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className={cn("w-[150px] justify-start text-left font-normal", !customDateFrom && "text-muted-foreground")}>
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {customDateFrom ? format(customDateFrom, 'dd/MM/yyyy') : 'Da'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={customDateFrom} onSelect={setCustomDateFrom} initialFocus className={cn("p-3 pointer-events-auto")} />
                  </PopoverContent>
                </Popover>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className={cn("w-[150px] justify-start text-left font-normal", !customDateTo && "text-muted-foreground")}>
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {customDateTo ? format(customDateTo, 'dd/MM/yyyy') : 'A'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={customDateTo} onSelect={setCustomDateTo} disabled={(date) => customDateFrom ? date < customDateFrom : false} initialFocus className={cn("p-3 pointer-events-auto")} />
                  </PopoverContent>
                </Popover>
              </>
            )}
            <div className="relative flex-1 min-w-[200px] max-w-[350px]">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Cerca per utente, pagina, tipo..."
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                className="pl-9 pr-9"
              />
              {searchText && (
                <button onClick={() => setSearchText('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            {(filterType !== 'all' || filterUser !== 'all' || searchText || dateRange === 'custom') && (
              <Button variant="ghost" size="sm" onClick={() => { setFilterType('all'); setFilterUser('all'); setSearchText(''); setDateRange('7'); setCustomDateFrom(undefined); setCustomDateTo(undefined); }}>
                Resetta filtri
              </Button>
            )}
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid gap-4 md:grid-cols-4">
          <SummaryCard
            icon={<Activity className="h-4 w-4 text-muted-foreground" />}
            title="Attività Totali"
            value={loading ? null : filtered.length}
          />
          <SummaryCard
            icon={<Users className="h-4 w-4 text-muted-foreground" />}
            title="Utenti Attivi"
            value={loading ? null : activeUserIds.size}
          />
          <SummaryCard
            icon={<UserCheck className="h-4 w-4 text-muted-foreground" />}
            title="Utenti Totali"
            value={loading ? null : profiles.size}
          />
          <SummaryCard
            icon={<UserX className="h-4 w-4 text-muted-foreground" />}
            title="Mai Usato (no ricerche)"
            value={loading ? null : inactiveUsers.length}
          />
        </div>

        {/* Charts row */}
        <div className="grid gap-4 md:grid-cols-2">
          {/* Daily trend */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                Andamento Giornaliero
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <Skeleton className="h-[250px] w-full" />
              ) : (
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={dailyTrend}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                    <XAxis dataKey="date" className="text-xs" />
                    <YAxis className="text-xs" />
                    <Tooltip />
                    <Line type="monotone" dataKey="count" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          {/* Activity breakdown pie */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Eye className="h-4 w-4" />
                Distribuzione Attività
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <Skeleton className="h-[250px] w-full" />
              ) : (
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={activityBreakdown}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={90}
                      paddingAngle={2}
                      dataKey="value"
                      nameKey="name"
                    >
                      {activityBreakdown.map((_, i) => (
                        <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              )}
              {!loading && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {activityBreakdown.slice(0, 6).map((item, i) => (
                    <Badge key={item.key} variant="outline" className="text-xs">
                      <span className="w-2 h-2 rounded-full mr-1" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                      {item.name}: {item.value}
                    </Badge>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Hourly distribution & Top users */}
        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Distribuzione Oraria
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <Skeleton className="h-[200px] w-full" />
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={hourlyDistribution}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                    <XAxis dataKey="hour" className="text-xs" interval={3} />
                    <YAxis className="text-xs" />
                    <Tooltip />
                    <Bar dataKey="count" fill="hsl(var(--primary))" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <SearchIcon className="h-4 w-4" />
                Top 10 Utenti Attivi
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <Skeleton className="h-[200px] w-full" />
              ) : (
                <div className="space-y-2 max-h-[200px] overflow-y-auto">
                  {topUsers.map((u, i) => (
                    <div key={u.userId} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-muted-foreground w-5 text-right">{i + 1}.</span>
                        <span className="truncate">{u.name}</span>
                      </div>
                      <Badge variant="secondary">{u.count}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Inactive users */}
        {!loading && inactiveUsers.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <UserX className="h-4 w-4 text-destructive" />
                Utenti senza ricerche/monitoraggi ({inactiveUsers.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Utente</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Registrato il</TableHead>
                    <TableHead>Ha navigato?</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {inactiveUsers.slice(0, 20).map(u => (
                    <TableRow key={u.id}>
                      <TableCell className="font-medium">{u.full_name || '-'}</TableCell>
                      <TableCell>{u.email}</TableCell>
                      <TableCell>{format(new Date(u.created_at), 'dd/MM/yyyy')}</TableCell>
                      <TableCell>
                        <Badge variant={u.hasAnyActivity ? 'default' : 'destructive'}>
                          {u.hasAnyActivity ? 'Sì' : 'No'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Recent activity log */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Attività Recenti</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-2">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            ) : (
              <div className="max-h-[400px] overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data/Ora</TableHead>
                      <TableHead>Utente</TableHead>
                      <TableHead>Azione</TableHead>
                      <TableHead>Pagina</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.slice(0, 100).map(a => {
                      const profile = profiles.get(a.user_id);
                      return (
                        <TableRow key={a.id}>
                          <TableCell className="text-xs whitespace-nowrap">
                            {format(new Date(a.created_at), 'dd/MM HH:mm:ss')}
                          </TableCell>
                          <TableCell className="text-sm">
                            {profile?.full_name || profile?.email || a.user_id.slice(0, 8)}
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant="outline"
                              style={{
                                borderColor: ACTIVITY_COLORS[a.activity_type] || '#6b7280',
                                color: ACTIVITY_COLORS[a.activity_type] || '#6b7280',
                              }}
                            >
                              {getActivityLabel(a.activity_type)}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">{a.page_path}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}

function SummaryCard({ icon, title, value }: { icon: React.ReactNode; title: string; value: number | null }) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        {icon}
      </CardHeader>
      <CardContent>
        {value === null ? <Skeleton className="h-8 w-16" /> : <div className="text-2xl font-bold">{value}</div>}
      </CardContent>
    </Card>
  );
}

function getActivityLabel(type: string): string {
  const labels: Record<string, string> = {
    page_view: 'Visualizzazione pagina',
    search: 'Ricerca',
    monitoring_create: 'Creazione monitoraggio',
    monitoring_run: 'Esecuzione monitoraggio',
    monitoring_toggle: 'Toggle monitoraggio',
    monitoring_delete: 'Eliminazione monitoraggio',
    result_view: 'Visualizzazione risultato',
    cpv_search: 'Ricerca CPV',
    settings_update: 'Aggiornamento impostazioni',
    support_ticket: 'Ticket supporto',
    alert_create: 'Creazione alert',
    alert_toggle: 'Toggle alert',
    export_pdf: 'Esportazione PDF',
    login: 'Login',
  };
  return labels[type] || type;
}
